/**
 * The anatomy of a turn on the widget (spec-chat-experience 15, §5.9) — a deliberate second copy of the
 * console's `entities/message/model/turnSlots.ts`: the widget shares no package with the console (D-2), so
 * this is two files, one order. Pure: no React, no components.
 *
 * Citations ("sources") nest inside the answer (D-47) — a nested slot, not a reordered one.
 */
export const TURN_SLOTS = ["trace", "answer", "result", "sources", "feedback"];

/** `assistant` is a second alias for `msg`. */
export const ANSWER_TYPES = new Set(["msg", "assistant"]);

/**
 * D-46: the legacy rich types keep the result slot until their producers retire. `data_collector` had NO
 * case at HEAD (a blank message despite `Receiver` styling for it — §6 G13); `widget` is a reply that is only
 * a Tier-1 widget (spec-widget-mcp 05). A legacy type that renders its own text occupies slots 2 and 3 as
 * one unit — no second answer is rendered above it.
 */
export const RESULT_TYPES = new Set([
	"msg_and_buttons",
	"gallery",
	"image",
	"contact_data_collector",
	"ticket_data_collector",
	"connect_data_collector",
	"data_collector",
	"widget",
]);

/** The slot a reply type renders in; `null` for a type nobody renders (it falls back to its text). */
export function slotOfType(type) {
	if (ANSWER_TYPES.has(type)) return "answer";
	if (RESULT_TYPES.has(type)) return "result";
	return null;
}

/**
 * What a reply with no renderer still says (§5.9 G-default, D-46): the envelope's `fallbackText`, else the
 * message the record carries. Never nothing — `default: console.warn` used to leave the turn blank.
 */
export function fallbackTextOf(content) {
	const v = content?.fallbackText ?? content?.widget?.fallbackText ?? content?.message ?? content?.config?.message;
	return typeof v === "string" ? v : "";
}

/**
 * Slot 1's inputs (runtime 08c): the live trace while streaming, then the collapsed summary — the persisted
 * one (`traceSummary`, from the record) or the live `summary` event. `null` when there is nothing to show,
 * so the slot renders no wrapper at all (§5.9 rule 5). Only what 08c whitelisted is passed on (D-43a).
 */
export function traceOf(content, traceSummary) {
	const streaming = content?.streaming === true && typeof content?._streamText === "string";
	const trace = streaming && content?._trace && content._trace.off === false ? content._trace : null;
	const summary = traceSummary ?? content?.traceSummary ?? null;
	const steps = content?.traceSteps;
	if (!trace && !summary) return null;
	return { trace, summary, steps, streaming };
}

/** The turn slot 6 may hang off: the newest record, once settled — a newer ask retires the chips at once. */
export function chipTurnIndex(records, isSettled) {
	const i = Array.isArray(records) ? records.length - 1 : -1;
	return i >= 0 && isSettled(records[i]) ? i : -1;
}

/** A record whose reply has finished (not a local echo, not still streaming). */
export const isSettledRecord = (record) => !!record?.reply && record.reply.streaming !== true && !record.pending;

/** Two chips, then a `+N` pill (D-47). */
export const VISIBLE_CITATIONS = 2;

/**
 * Every citation the visitor may open, from `sourceDocuments`, `config.sourceDocuments` or a JSON
 * `fullMessage`'s `source_documents`; raw file links and name-is-the-url rows are dropped (HEAD's rule).
 */
export function citationsOf(content) {
	let list = [];
	if (Array.isArray(content?.sourceDocuments)) list = content.sourceDocuments;
	else if (Array.isArray(content?.config?.sourceDocuments)) list = content.config.sourceDocuments;
	else if (typeof content?.fullMessage === "string") {
		try {
			const parsed = JSON.parse(content.fullMessage);
			if (Array.isArray(parsed.source_documents)) list = parsed.source_documents;
		} catch (e) {
			console.warn("Invalid fullMessage JSON", e);
		}
	}
	return list
		.filter((src) => src && typeof src === "object")
		.map(({ name = "", url = "" }) => ({ name, url }))
		.filter(({ name, url }) => name && name !== url && !/\.(pdf|docx|csv)$/i.test(url));
}
